import { useCallback } from 'react'
import {
  useIsFetching,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import backend from '@api/backend'
import {
  notifyError,
  notifySuccess,
} from '@common/utils/notify'
import { hasPermissions } from '@common/utils/validation/authValidators'
import {
  AlertAPI,
  AlertGroup,
  AlertQuery,
  AlertStat,
  AlertStatus,
  HistoricalAlert,
  HistoricalAlertsApi,
} from '@data/alerts/model/alert.model'
import { alertCommentsQueryKey } from '@data/comments/alertComments/alertComments'
import { DeviceIDTypes } from '@data/device/model/device'
import {
  mapAlertGroups,
  mapHistoricalAlertGroup,
  mapHistoricalAlerts,
} from '@data/alerts/mapAlerts'
import { useAuthStore } from '@store/auth.store'

export const ALERTS_QUERY_KEY = ['alerts'] as const
export const ACTIVE_ALERTS_QUERY_KEY = ['alerts', 'active'] as const
export const ALERT_HISTORY_QUERY_KEY = ['alerts', 'history'] as const
export const ALERT_STATS_QUERY_KEY = ['alerts', 'stats'] as const
// Nested under the history prefix so the acknowledgement mutation's existing
// invalidateQueries({ queryKey: ALERT_HISTORY_QUERY_KEY }) reaches cached group detail too.
export const ALERT_HISTORY_DETAIL_QUERY_KEY = ['alerts', 'history', 'detail'] as const
export const ACKNOWLEDGE_ALERTS_MUTATION_KEY = ['alerts', 'acknowledge'] as const
export const REFRESH_ALERT_GROUP_MUTATION_KEY = ['alerts', 'refresh-group'] as const

export interface AlertStatsQuery {
  deviceIds: string[]
  startDate: string | null
  endDate: string | null
  idType: DeviceIDTypes
}

export interface AlertHistoryDetailQuery {
  dUuid: string
  gid: string
  uom: string
}

export interface AcknowledgeAlertsVariables {
  alertGroup: AlertGroup
  priorityTier: number
  status: AlertStatus
  excludeUsage: boolean
}

export const alertHistoryQueryKey = (query: AlertQuery) => [
  ...ALERT_HISTORY_QUERY_KEY,
  {
    startDate: query.startDate,
    endDate: query.endDate,
    ackResult: [...query.ackResult].sort((a, b) => a - b),
  },
] as const

export const alertStatsQueryKey = (query: AlertStatsQuery) => [
  ...ALERT_STATS_QUERY_KEY,
  {
    deviceIds: [...query.deviceIds].sort((a, b) => a.localeCompare(b)),
    startDate: query.startDate,
    endDate: query.endDate,
    idType: query.idType,
  },
] as const

export const alertHistoryDetailQueryKey = (query: AlertHistoryDetailQuery) => [
  ...ALERT_HISTORY_DETAIL_QUERY_KEY,
  {
    dUuid: query.dUuid,
    gid: query.gid,
    uom: query.uom,
  },
] as const

const getActiveAlerts = async (signal: AbortSignal): Promise<AlertGroup[]> => {
  const response = await backend.get<{ data: AlertAPI[] }>('/usage_alerts', {
    signal,
    params: { status: ['open', 'in_progress'] },
    paramsSerializer: { indexes: null },
  })

  return mapAlertGroups(response.data.data)
}

// GET /usage_alerts/{device_id} — the same payload as the queue, scoped to one device.
// Used to refresh a single group without refetching the whole queue.
const getDeviceActiveAlerts = async (dUuid: string): Promise<AlertGroup[]> => {
  const response = await backend.get<{ data: AlertAPI[] }>(`/usage_alerts/${dUuid}`, {
    params: {
      idType: DeviceIDTypes.dUuid,
      status: ['open', 'in_progress'],
    },
    paramsSerializer: { indexes: null },
  })

  return mapAlertGroups(response.data.data)
}

const getHistoricalAlerts = async (
  query: AlertQuery,
  signal: AbortSignal,
): Promise<HistoricalAlert[]> => {
  const response = await backend.get<HistoricalAlertsApi[]>('/usage_alerts/history', {
    signal,
    params: query,
    paramsSerializer: { indexes: null },
  })

  return mapHistoricalAlerts(response.data)
}

const getHistoricalAlertGroup = async (
  query: AlertHistoryDetailQuery,
  signal: AbortSignal,
): Promise<AlertAPI[]> => {
  const response = await backend.get<{ data: AlertAPI[] }>(
    `/usage_alerts/${query.dUuid}/history/${query.gid}`,
    {
      signal,
      params: {
        // `idType`, not `id_type`. The component this moved out of sent the snake_case
        // spelling; the API accepts both today, but `id_type` is a legacy alias due for
        // removal and is not in the OpenAPI document. See
        // docs/api-contract-follow-ups.md.
        idType: DeviceIDTypes.dUuid,
        uom: query.uom,
      },
    },
  )

  return response.data.data
}

const getAlertStats = async (
  query: AlertStatsQuery,
  signal: AbortSignal,
): Promise<AlertStat[]> => {
  const responses = await Promise.all(
    query.deviceIds.map((deviceId) =>
      backend.get<AlertStat[]>(`/usage_alerts/${deviceId}/stats`, {
        signal,
        params: {
          startDate: query.startDate,
          endDate: query.endDate,
          idType: query.idType,
        },
      }),
    ),
  )

  return responses.flatMap((response) => response.data)
}

const acknowledgeAlerts = async ({
  alertGroup,
  priorityTier,
  status,
  excludeUsage,
}: AcknowledgeAlertsVariables): Promise<void> => {
  await backend.patch(`/usage_alerts/${alertGroup.deviceId}`, {
    groupId: alertGroup.gid,
    alerts: alertGroup.alerts.map((alert) => ({
      id: alert.id,
      ackResult: priorityTier,
      status: status === AlertStatus.CLOSED ? 'closed' : 'in_progress',
      valid: alert.valid,
      excludeUsage,
    })),
  })
}

export const updateActiveAlertsAfterAcknowledge = (
  current: AlertGroup[],
  variables: AcknowledgeAlertsVariables,
): AlertGroup[] => {
  if (variables.status === AlertStatus.CLOSED) {
    return current.filter((group) => group.gid !== variables.alertGroup.gid)
  }

  return current.map((group) => {
    if (group.gid !== variables.alertGroup.gid) return group

    return {
      ...group,
      priorityTier: variables.priorityTier,
      status: variables.status,
      alerts: group.alerts.map((alert) => ({
        ...alert,
        priorityTier: variables.priorityTier,
        status: variables.status,
        excludeUsage: variables.excludeUsage,
      })),
    }
  })
}

// Replaces one group in the cached queue with a freshly fetched copy, or drops it when the
// server no longer returns it as active — someone else closed it. Sibling groups on the same
// device are left alone; use the whole-queue refresh to pick up groups that did not exist
// when the queue was loaded.
export const replaceActiveAlertGroup = (
  current: AlertGroup[],
  gid: string,
  refreshed: AlertGroup | undefined,
): AlertGroup[] => {
  if (!refreshed) return current.filter((group) => group.gid !== gid)

  return current.map((group) => (group.gid === gid ? refreshed : group))
}

export const useActiveAlertsQuery = () => {
  const isSessionReady = useAuthStore((state) => state.authenticated)
  const canReadActiveAlerts = useAuthStore((state) =>
    state.permissions.some((permission) =>
      [
        'READ:ALERTS:USAGE:OPEN',
        'READ:ALERTS:USAGE:IN_PROGRESS',
        'READ:ALERTS:USAGE:CLOSED',
      ].includes(permission.name),
    ),
  )

  return useQuery<AlertGroup[]>({
    queryKey: ACTIVE_ALERTS_QUERY_KEY,
    queryFn: ({ signal }) => getActiveAlerts(signal),
    enabled: isSessionReady && canReadActiveAlerts,
    staleTime: Infinity,
  })
}

// Pull in alerts raised since the queue was loaded.
//
// The active query is `staleTime: Infinity` — deliberately, so selection changes and remounts
// never refetch it — which also means it never picks up new alerts on its own. That is why
// seeing a new alert has meant reloading the whole dashboard. Invalidating overrides the
// stale time for mounted observers, so this is the supported way to ask for fresh data.
//
// Callable from anywhere, including screens that do not render the queue themselves.
export const useRefreshActiveAlerts = () => {
  const queryClient = useQueryClient()
  const isRefreshing = useIsFetching({ queryKey: ACTIVE_ALERTS_QUERY_KEY }) > 0

  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ACTIVE_ALERTS_QUERY_KEY }),
    [queryClient],
  )

  return {
    refresh,
    isRefreshing,
  }
}

// Re-read one group from the device-scoped endpoint and splice it back into the queue,
// instead of refetching every alert to update one of them.
//
// A mutation rather than a query: it is user-initiated, runs once per press, and its
// `isPending`/`error` are what a refresh control needs. The permission gate mirrors the
// active query's, since it reads the same data.
export const useRefreshAlertGroup = () => {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((state) => state.permissions)

  return useMutation<AlertGroup[], Error, { dUuid: string, gid: string }>({
    mutationKey: REFRESH_ALERT_GROUP_MUTATION_KEY,
    mutationFn: ({ dUuid }) => {
      const canRead = permissions.some((permission) =>
        [
          'READ:ALERTS:USAGE:OPEN',
          'READ:ALERTS:USAGE:IN_PROGRESS',
          'READ:ALERTS:USAGE:CLOSED',
        ].includes(permission.name),
      )

      if (!canRead) throw new Error('You do not have permission to read alerts')

      return getDeviceActiveAlerts(dUuid)
    },
    onSuccess: (groups, { dUuid, gid }) => {
      queryClient.setQueryData<AlertGroup[]>(ACTIVE_ALERTS_QUERY_KEY, (current = []) =>
        replaceActiveAlertGroup(current, gid, groups.find((group) => group.gid === gid)),
      )
      // Comments are a separate query on the same group, and go stale for the same reason
      // the group does — someone else added one. Invalidated rather than refetched here so
      // it only costs a request when the group's comments are actually on screen.
      void queryClient.invalidateQueries({ queryKey: alertCommentsQueryKey(dUuid, gid) })
    },
  })
}

export const useHistoricalAlertsQuery = (query: AlertQuery, enabled = true) => {
  const isSessionReady = useAuthStore((state) => state.authenticated)
  const canReadHistory = useAuthStore((state) =>
    hasPermissions(state.permissions, ['READ:ALERTS:USAGE:HISTORY']),
  )

  return useQuery<HistoricalAlert[]>({
    queryKey: alertHistoryQueryKey(query),
    queryFn: ({ signal }) =>
      canReadHistory ? getHistoricalAlerts(query, signal) : Promise.resolve([]),
    enabled: enabled && isSessionReady && canReadHistory,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 120,
  })
}

// `device` and `selection` shape the mapped group but not the request, so they stay out of
// the query key. A given gid always belongs to one device.
export const useHistoricalAlertGroupQuery = (
  query: AlertHistoryDetailQuery | undefined,
  device: { deviceId: string, deviceName: string, uom: string } | undefined,
  priorityTier: number | undefined,
) => {
  const isSessionReady = useAuthStore((state) => state.authenticated)
  const canReadHistory = useAuthStore((state) =>
    hasPermissions(state.permissions, ['READ:ALERTS:USAGE:HISTORY']),
  )

  return useQuery<AlertGroup | null>({
    queryKey: query ? alertHistoryDetailQueryKey(query) : ALERT_HISTORY_DETAIL_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const alerts = await getHistoricalAlertGroup(query!, signal)
      return mapHistoricalAlertGroup(alerts, device!, { priorityTier: priorityTier! })
    },
    enabled: !!query && !!device && priorityTier !== undefined && isSessionReady && canReadHistory,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 120,
  })
}

export const useAlertStatsQuery = (query: AlertStatsQuery, enabled = true) => {
  const isSessionReady = useAuthStore((state) => state.authenticated)
  const canReadHistory = useAuthStore((state) =>
    hasPermissions(state.permissions, ['READ:ALERTS:USAGE:HISTORY']),
  )

  return useQuery<AlertStat[]>({
    queryKey: alertStatsQueryKey(query),
    queryFn: ({ signal }) => canReadHistory ? getAlertStats(query, signal) : Promise.resolve([]),
    enabled: enabled && isSessionReady && canReadHistory && query.deviceIds.length > 0,
  })
}

export const useAcknowledgeAlertsMutation = () => {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((state) => state.permissions)

  return useMutation<void, Error, AcknowledgeAlertsVariables>({
    mutationKey: ACKNOWLEDGE_ALERTS_MUTATION_KEY,
    mutationFn: async (variables) => {
      if (!hasPermissions(permissions, ['UPDATE:ALERTS:USAGE'])) {
        throw new Error('You do not have permission to acknowledge alerts')
      }

      return acknowledgeAlerts(variables)
    },
    onSuccess: (_, variables) => {
      queryClient.setQueryData<AlertGroup[]>(ACTIVE_ALERTS_QUERY_KEY, (current = []) =>
        updateActiveAlertsAfterAcknowledge(current, variables),
      )
      void queryClient.invalidateQueries({ queryKey: ALERT_HISTORY_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: ALERT_STATS_QUERY_KEY })
      notifySuccess('Success')
    },
    onError: () => {
      notifyError('Something went wrong, please try again', {
        autoClose: 5000,
      })
    },
  })
}
